// Features: MCRO001-F0028, MCRO001-F0029
// Tests: T-MCRO-028, T-MCRO-029

import { projectMappedDemonstration } from './mappedDemonstrationProjection';
import { deterministicTrackColour } from './mediaTrackColour';

const finite = (value) => value !== null && value !== undefined && value !== ''
  && Number.isFinite(Number(value)) ? Number(value) : null;

// F0029 focus-on activation withholds/occludes a mapped demonstration once its
// effective video rate exceeds this threshold. Server config (capabilities API)
// may override this per-context via an optional `focusOnRateThreshold` key on
// the context/options object threaded through isRateRenderableAtCount and
// focusOnClassification; any missing/non-finite/non-positive override falls
// back to this default so unset callers behave exactly as before.
export const DEFAULT_FOCUS_ON_RATE_THRESHOLD = 1.3;

function resolveFocusOnRateThreshold(context) {
  const candidateValue = finite(context?.focusOnRateThreshold);
  return candidateValue !== null && candidateValue > 0
    ? candidateValue
    : DEFAULT_FOCUS_ON_RATE_THRESHOLD;
}

export function demonstrationPriority(candidate, fallback = 0) {
  const priority = finite(candidate?.priority);
  return priority === null ? fallback : priority;
}

export function orderedDemonstrations(candidates = []) {
  return candidates
    .map((candidate, index) => ({ ...candidate, priority: demonstrationPriority(candidate, index) }))
    .sort((left, right) => left.priority - right.priority
      || left.demonstrationId.localeCompare(right.demonstrationId));
}

export function rangesOverlap(left, right) {
  return left?.timingMap?.startCount <= right?.timingMap?.endCount
    && right?.timingMap?.startCount <= left?.timingMap?.endCount;
}

export function demonstrationLanes(candidates = []) {
  const positioned = [];
  orderedDemonstrations(candidates).forEach((candidate) => {
    const lowerOverlaps = positioned.filter((item) => rangesOverlap(item, candidate));
    // A higher-priority line must sit strictly farther into the Canvas than
    // every lower-priority line it overlaps. This intentionally leaves holes
    // in chained-overlap layouts instead of visually reversing a pair.
    const lane = lowerOverlaps.length === 0
      ? 0
      : Math.max(...lowerOverlaps.map((item) => item.lane)) + 1;
    positioned.push({ ...candidate, lane });
  });
  return positioned;
}

function mergedCoverage(intervals) {
  return intervals
    .sort((left, right) => left.start - right.start || left.end - right.end)
    .reduce((merged, interval) => {
      const previous = merged.at(-1);
      if (!previous || interval.start > previous.end + 1) merged.push({ ...interval });
      else previous.end = Math.max(previous.end, interval.end);
      return merged;
    }, []);
}

function isRateRenderableAtCount(candidate, count, context = {}) {
  const { bpm, rehearsalRate, revisionId, supportsPlaybackRate } = context;
  if (candidate?.authorityEligible !== true) return false;
  try {
    const projection = projectMappedDemonstration({
      timingMap: candidate.timingMap,
      count,
      bpm,
      rehearsalRate,
      revisionId,
    });
    if (!projection.visible || projection.effectiveVideoRate > resolveFocusOnRateThreshold(context)) return false;
    return typeof supportsPlaybackRate !== 'function'
      || supportsPlaybackRate(projection.effectiveVideoRate) === true;
  } catch (_) {
    return false;
  }
}

export function isCompletelyOccluded(candidate, candidates = [], context = null) {
  if (!candidate?.timingMap) return false;
  const priority = demonstrationPriority(candidate);
  const higher = candidates
    .filter((other) => other.demonstrationId !== candidate.demonstrationId
      && demonstrationPriority(other) > priority
      && rangesOverlap(candidate, other));
  const covering = mergedCoverage(higher.flatMap((other) => {
    const start = Math.max(candidate.timingMap.startCount, other.timingMap.startCount);
    const end = Math.min(candidate.timingMap.endCount, other.timingMap.endCount);
    // Range-only callers are used by rail layout tests. Rehearsal always supplies
    // authority/rate context and therefore takes the actor-safe branch below.
    if (!context) return [{ start, end }];
    const points = Array.isArray(other?.timingMap?.syncPoints) ? other.timingMap.syncPoints : [];
    return points.slice(0, -1).flatMap((left, index) => {
      const right = points[index + 1];
      const segmentStart = Math.max(Math.ceil(start), left.count);
      const segmentEnd = Math.min(
        Math.floor(end),
        index === points.length - 2 ? right.count : right.count - 1,
      );
      if (segmentStart > segmentEnd || !isRateRenderableAtCount(other, segmentStart, context)) return [];
      return [{ start: segmentStart, end: segmentEnd }];
    });
  }));
  if (covering.length === 0 || covering[0].start > candidate.timingMap.startCount) return false;
  return covering.reduce((coveredEnd, interval) => interval.start <= coveredEnd + 1
    ? Math.max(coveredEnd, interval.end)
    : coveredEnd, covering[0].end) >= candidate.timingMap.endCount;
}

export function focusOnClassification(candidate, candidates, {
  count,
  bpm,
  rehearsalRate,
  revisionId,
  explicitFocusId = '',
  supportsPlaybackRate,
  focusOnRateThreshold,
} = {}) {
  const threshold = resolveFocusOnRateThreshold({ focusOnRateThreshold });
  const occluded = isCompletelyOccluded(candidate, candidates, {
    bpm,
    rehearsalRate,
    revisionId,
    supportsPlaybackRate,
    focusOnRateThreshold,
  });
  let projection = { visible: false };
  try {
    projection = projectMappedDemonstration({
      timingMap: candidate.timingMap,
      count,
      bpm,
      rehearsalRate,
      revisionId,
      allowExplicitFocusRate: candidate.demonstrationId === explicitFocusId,
    });
  } catch (_) {
    projection = { visible: false };
  }
  const rate = projection.visible && projection.effectiveVideoRate > threshold;
  let startProjection = { visible: false };
  if (rate) {
    try {
      startProjection = projectMappedDemonstration({
        timingMap: candidate.timingMap,
        count: candidate.timingMap.startCount,
        bpm,
        rehearsalRate,
        revisionId,
        allowExplicitFocusRate: candidate.demonstrationId === explicitFocusId,
      });
    } catch (_) {
      startProjection = { visible: false };
    }
  }
  return {
    focusOn: rate || occluded,
    reasons: [rate ? 'rate' : null, occluded ? 'occlusion' : null].filter(Boolean),
    projection,
    targetRehearsalRate: rate && startProjection.visible && startProjection.segmentBaseVideoRate > 0
      ? 1 / startProjection.segmentBaseVideoRate
      : null,
  };
}

export function resolveDemonstrationPriority(candidates = [], context = {}) {
  const ordered = orderedDemonstrations(candidates);
  const active = ordered.filter(({ timingMap }) => Number(context.count) >= timingMap.startCount
    && Number(context.count) <= timingMap.endCount);
  const classified = active.map((candidate) => ({
    candidate,
    classification: focusOnClassification(candidate, ordered, context),
  }));
  // Explicit focus is a session-only render selection. It deliberately overrides
  // durable priority without altering the saved order.
  const explicit = classified.find(({ candidate }) => candidate.demonstrationId === context.explicitFocusId);
  const selected = explicit?.candidate || [...classified]
    .reverse()
    .find(({ classification }) => !classification.focusOn)?.candidate || null;
  return {
    ordered,
    selected,
    focusOn: classified.filter(({ classification }) => classification.focusOn),
  };
}

export function focusPlaybackRateForCount(timingMap, count, bpm, revisionId) {
  const projection = projectMappedDemonstration({
    timingMap,
    count,
    bpm,
    rehearsalRate: 1,
    revisionId,
    allowExplicitFocusRate: true,
  });
  const target = projection.visible && projection.segmentBaseVideoRate > 0
    ? 1 / projection.segmentBaseVideoRate
    : null;
  if (!Number.isFinite(target) || target <= 0) return null;
  return { rate: target, segmentIndex: projection.segmentIndex };
}

export function priorityOrderWithPromotion(candidates = [], demonstrationId) {
  const ids = orderedDemonstrations(candidates).map(({ demonstrationId: id }) => id);
  if (!ids.includes(demonstrationId)) return ids;
  return [...ids.filter((id) => id !== demonstrationId), demonstrationId];
}

export function applyPriorityOrder(candidates = [], priorityOrder = []) {
  const ranks = new Map(priorityOrder.map((id, index) => [id, index]));
  return candidates.map((candidate, index) => ({
    ...candidate,
    priority: ranks.has(candidate.demonstrationId)
      ? ranks.get(candidate.demonstrationId)
      : priorityOrder.length + demonstrationPriority(candidate, index),
  }));
}

// Legacy demonstrationId-keyed colour map. As of MCRO001-F0052 the single colour
// source lives in mediaTrackColour.js (keyed by the video's mediaId so the Canvas
// rail and the Video Library tile border agree); this wrapper preserves the
// historical `{ [demonstrationId]: colour }` shape for any remaining consumer and
// its test by reusing that ONE hash/persisted-colour logic — there is no second
// palette. It differs only in key: demonstrationId here, mediaId in the rail/library.
export function stableDemonstrationColours(candidates = []) {
  return candidates.reduce((colours, candidate) => {
    const persisted = typeof candidate.colour === 'string' ? candidate.colour.trim() : '';
    colours[candidate.demonstrationId] = persisted || deterministicTrackColour(candidate.demonstrationId);
    return colours;
  }, {});
}
